import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { X, Info, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const AnnouncementBanner = ({ onHeightChange }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [modalAnn, setModalAnn] = useState(null);
  const [shownToasts, setShownToasts] = useState([]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/announcements/active`);
      const allAnnouncements = res.data.announcements || [];
      
      const dismissedIds = JSON.parse(localStorage.getItem('dismissed_announcements') || '[]');
      setDismissed(dismissedIds);
      setAnnouncements(allAnnouncements);
      
      // Show toast announcements (once per session)
      const sessionShown = JSON.parse(sessionStorage.getItem('shown_toast_announcements') || '[]');
      allAnnouncements
        .filter(ann => ann.display_type === 'toast' && !dismissedIds.includes(ann.announcement_id) && !sessionShown.includes(ann.announcement_id))
        .forEach(ann => {
          const toastFn = ann.type === 'error' ? toast.error : ann.type === 'warning' ? toast.warning : ann.type === 'success' ? toast.success : toast.info;
          toastFn(`${ann.title}: ${ann.message}`, { duration: 8000 });
          sessionShown.push(ann.announcement_id);
        });
      sessionStorage.setItem('shown_toast_announcements', JSON.stringify(sessionShown));
      
      // Show modal announcement (first undismissed modal)
      const firstModal = allAnnouncements.find(
        ann => ann.display_type === 'modal' && !dismissedIds.includes(ann.announcement_id)
      );
      if (firstModal) setModalAnn(firstModal);
    } catch (error) {
      // Deleteent fail
    }
  }, []);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);


  const handleDismiss = useCallback((announcementId) => {
    const newDismissed = [...dismissed, announcementId];
    setDismissed(newDismissed);
    localStorage.setItem('dismissed_announcements', JSON.stringify(newDismissed));
  }, [dismissed]);

  const handleModalClose = () => {
    if (modalAnn) {
      if (modalAnn.is_dismissible) handleDismiss(modalAnn.announcement_id);
      setModalAnn(null);
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'success':
        return { bg: 'bg-emerald-50 border-b border-emerald-100', textClass: 'text-emerald-800', icon: <CheckCircle className="w-5 h-5 text-emerald-600" /> };
      case 'warning':
        return { bg: 'bg-orange-50 border-b border-orange-100', textClass: 'text-orange-800', icon: <AlertTriangle className="w-5 h-5 text-orange-600" /> };
      case 'error':
        return { bg: 'bg-red-50 border-b border-red-100', textClass: 'text-red-800', icon: <AlertCircle className="w-5 h-5 text-red-600" /> };
      default:
        return { bg: 'bg-emerald-50 border-b border-emerald-100', textClass: 'text-emerald-800', icon: <Info className="w-5 h-5 text-emerald-600" /> };
    }
  };

  const bannerAnnouncements = announcements.filter(
    ann => ann.display_type === 'banner' && !dismissed.includes(ann.announcement_id)
  );

  useEffect(() => {
    if (onHeightChange) {
      onHeightChange(bannerAnnouncements.length > 0 ? bannerAnnouncements.length * 44 : 0);
    }
  }, [bannerAnnouncements.length, onHeightChange]);

  return (
    <>
      {/* Banner announcements */}
      {bannerAnnouncements.length > 0 && (
        <aside
          role="complementary"
          aria-label="Site announcements"
          className="fixed top-0 left-0 right-0 z-[60] w-full"
        >
          {bannerAnnouncements.map((ann) => {
            const styles = getTypeStyles(ann.type);
            return (
              <div
                key={ann.announcement_id}
                className={`${styles.bg} py-2.5 px-4`}
                data-testid={`announcement-banner-${ann.announcement_id}`}
              >
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                  <div className={`flex items-center gap-3 flex-1 ${styles.textClass || 'text-slate-800'}`}>
                    {styles.icon}
                    <div className="flex-1">
                      <span className="font-semibold mr-2 text-sm">{ann.title}</span>
                      <span className="text-sm opacity-80">{ann.message}</span>
                    </div>
                  </div>
                  {ann.is_dismissible && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 ${styles.textClass || 'text-slate-600'} hover:bg-black/5`}
                      onClick={() => handleDismiss(ann.announcement_id)}
                      data-testid={`dismiss-announcement-${ann.announcement_id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </aside>
      )}

      {/* Modal announcement */}
      {modalAnn && (
        <Dialog open={!!modalAnn} onOpenChange={handleModalClose}>
          <DialogContent className="max-w-md" data-testid={`announcement-modal-${modalAnn.announcement_id}`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {getTypeStyles(modalAnn.type).icon}
                {modalAnn.title}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">{modalAnn.message}</p>
            {modalAnn.is_dismissible && (
              <Button onClick={handleModalClose} className="mt-2 w-full">OK</Button>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AnnouncementBanner;
